import React from 'react';
import { cn } from '@/utils/cn';
import { AlertCircleIcon } from '@/components/UI/icons';

export interface FieldShellProps {
  /** id of the control this shell labels. The hint and error get `${id}-hint` / `${id}-error`. */
  id: string;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** What to put in the control's `aria-describedby` so the hint or error is read with it. */
export function describedBy(id: string, hint?: React.ReactNode, error?: React.ReactNode): string | undefined {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

/**
 * Label above, control, then a hint or, if invalid, an error with an icon so colour is not the only cue.
 * Shared by Input, Select and Textarea so every field in the app is laid out and announced the same way.
 */
export const FieldShell: React.FC<FieldShellProps> = ({ id, label, hint, error, required, className, children }) => (
  <div className={cn('w-full', className)}>
    {label && (
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ui-text">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-danger-text">
            *
          </span>
        )}
      </label>
    )}
    {children}
    {error ? (
      <p id={`${id}-error`} className="mt-1.5 flex items-start gap-1.5 text-xs text-danger-text">
        <AlertCircleIcon size={14} className="mt-px shrink-0" />
        <span>{error}</span>
      </p>
    ) : hint ? (
      <p id={`${id}-hint`} className="mt-1.5 text-xs text-ui-text2">
        {hint}
      </p>
    ) : null}
  </div>
);
